import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useAuth } from "@/hooks/use-auth";
import { formatPrice } from "@/lib/format";
import { customerTrackLink } from "@/lib/whatsapp";
import { MessageCircle } from "lucide-react";

export const Route = createFileRoute("/orders")({
  head: () => ({ meta: [{ title: "My Orders — Jul's Skin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: OrdersPage,
});

function OrdersPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth", search: { redirect: "/orders" } }); }, [user, loading]);

  const { data: orders } = useQuery({
    queryKey: ["my-orders", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*, order_items(*)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="mx-auto max-w-5xl w-full px-6 py-16">
        <h1 className="display text-4xl md:text-5xl">Your orders</h1>
        <div className="mt-12 space-y-6">
          {orders?.length === 0 && <p className="text-muted-foreground">No orders yet. <Link to="/shop" className="underline">Start shopping →</Link></p>}
          {orders?.map((o: any) => (
            <div key={o.id} className="border border-border p-6">
              <div className="flex flex-wrap justify-between gap-3 text-sm">
                <div>
                  <p className="eyebrow text-muted-foreground">Order</p>
                  <p className="font-mono mt-1">{o.id.slice(0, 8)}</p>
                </div>
                <div>
                  <p className="eyebrow text-muted-foreground">Placed</p>
                  <p className="mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="eyebrow text-muted-foreground">Total</p>
                  <p className="mt-1 tabular-nums">{formatPrice(o.total_cents)}</p>
                </div>
                <div>
                  <p className="eyebrow text-muted-foreground">Status</p>
                  <p className="mt-1 capitalize">{o.status.replace("_", " ")}</p>
                </div>
              </div>
              <div className="hairline mt-6 pt-4 space-y-2 text-sm text-muted-foreground">
                {o.order_items.map((it: any) => (
                  <div key={it.id} className="flex justify-between">
                    <span>{it.product_name} × {it.quantity} / {it.size}</span>
                    <span className="tabular-nums">{formatPrice(it.unit_price_cents * it.quantity)}</span>
                  </div>
                ))}
              </div>
              {(o.tracking_number || o.tracking_url || o.courier) && (
                <div className="hairline mt-4 pt-4 text-sm">
                  <p className="eyebrow text-muted-foreground mb-2">Tracking</p>
                  {o.courier && <p>Courier: <span className="text-foreground">{o.courier}</span></p>}
                  {o.tracking_number && <p>AWB: <span className="font-mono">{o.tracking_number}</span></p>}
                  {o.tracking_url && (
                    <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline">
                      Track shipment →
                    </a>
                  )}
                </div>
              )}
              <div className="hairline mt-4 pt-4 flex flex-wrap gap-2">
                {o.whatsapp_tracking_id && (
                  <a
                    href={/^https?:\/\//i.test(o.whatsapp_tracking_id) ? o.whatsapp_tracking_id : `https://wa.me/${o.whatsapp_tracking_id.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 bg-[#25D366] text-white hover:opacity-90 rounded"
                  >
                    <MessageCircle className="h-4 w-4" /> Track on WhatsApp
                  </a>
                )}
                {!o.whatsapp_tracking_id && (
                  <a
                    href={customerTrackLink(o)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-border hover:bg-muted rounded"
                  >
                    <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1" /><SiteFooter />
    </div>
  );
}
